import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
} from "@material-ui/core";
import {
  GetApp as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Label as LabelIcon,
} from "@material-ui/icons";
import { format, subDays } from "date-fns";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  cardContainer: {
    marginBottom: theme.spacing(3),
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  cardIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  cardValue: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.text.primary,
  },
  cardLabel: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  filterSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  tableSection: {
    marginTop: theme.spacing(3),
  },
  tableHeader: {
    backgroundColor: theme.palette.background.default,
    fontWeight: "bold",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  noData: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const UTMDashboard = () => {
  const classes = useStyles();

  const [dateStart, setDateStart] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [dateEnd, setDateEnd] = useState(format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalContacts: 0,
    totalTickets: 0,
    uniqueSources: 0,
    uniqueCampaigns: 0,
  });
  const [metrics, setMetrics] = useState([]);

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/utm/report", {
        params: {
          dateStart,
          dateEnd,
        },
      });

      setSummary(data.summary || {
        totalContacts: 0,
        totalTickets: 0,
        uniqueSources: 0,
        uniqueCampaigns: 0,
      });
      setMetrics(data.metrics || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (metrics.length === 0) {
      toast.warning(i18n.t("utm.dashboard.noDataToExport"));
      return;
    }

    // Criar CSV
    const headers = [
      i18n.t("utm.dashboard.table.source"),
      i18n.t("utm.dashboard.table.campaign"),
      i18n.t("utm.dashboard.table.contacts"),
      i18n.t("utm.dashboard.table.tickets"),
      i18n.t("utm.dashboard.table.open"),
      i18n.t("utm.dashboard.table.closed"),
      i18n.t("utm.dashboard.table.firstContact"),
      i18n.t("utm.dashboard.table.lastContact"),
    ];

    const csvRows = [headers.join(",")];

    metrics.forEach((metric) => {
      const row = [
        `"${metric.utm_source || ""}"`,
        `"${metric.utm_campaign || ""}"`,
        metric.contacts || 0,
        metric.tickets || 0,
        metric.openTickets || 0,
        metric.closedTickets || 0,
        metric.firstContact ? format(new Date(metric.firstContact), "dd/MM/yyyy HH:mm") : "",
        metric.lastContact ? format(new Date(metric.lastContact), "dd/MM/yyyy HH:mm") : "",
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", `utm_report_${dateStart}_${dateEnd}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(i18n.t("utm.dashboard.exportSuccess"));
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("utm.dashboard.title")}</Title>
        <MainHeaderButtonsWrapper></MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Filtros de Período */}
        <Paper className={classes.filterSection} variant="outlined">
          <Typography variant="h6" gutterBottom>
            {i18n.t("utm.dashboard.period")}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                label={i18n.t("utm.dashboard.dateStart")}
                type="date"
                variant="outlined"
                fullWidth
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={i18n.t("utm.dashboard.dateEnd")}
                type="date"
                variant="outlined"
                fullWidth
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={loadReport}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : i18n.t("utm.dashboard.loadReport")}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {/* Cards de Resumo */}
            <Grid container spacing={2} className={classes.cardContainer}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <PeopleIcon className={classes.cardIcon} />
                    <Typography className={classes.cardValue}>
                      {summary.totalContacts}
                    </Typography>
                    <Typography className={classes.cardLabel}>
                      {i18n.t("utm.dashboard.cards.totalContacts")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <AssignmentIcon className={classes.cardIcon} />
                    <Typography className={classes.cardValue}>
                      {summary.totalTickets}
                    </Typography>
                    <Typography className={classes.cardLabel}>
                      {i18n.t("utm.dashboard.cards.totalTickets")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <TrendingUpIcon className={classes.cardIcon} />
                    <Typography className={classes.cardValue}>
                      {summary.uniqueSources}
                    </Typography>
                    <Typography className={classes.cardLabel}>
                      {i18n.t("utm.dashboard.cards.totalSources")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <LabelIcon className={classes.cardIcon} />
                    <Typography className={classes.cardValue}>
                      {summary.uniqueCampaigns}
                    </Typography>
                    <Typography className={classes.cardLabel}>
                      {i18n.t("utm.dashboard.cards.totalCampaigns")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tabela de Métricas */}
            <Paper className={classes.tableSection} variant="outlined">
              <div style={{ padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6">
                  {i18n.t("utm.dashboard.metricsTable")}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  disabled={metrics.length === 0}
                >
                  {i18n.t("utm.dashboard.export")}
                </Button>
              </div>

              {metrics.length === 0 ? (
                <div className={classes.noData}>
                  <Typography variant="body1">
                    {i18n.t("utm.dashboard.noData")}
                  </Typography>
                </div>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeader}>
                        {i18n.t("utm.dashboard.table.source")}
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        {i18n.t("utm.dashboard.table.campaign")}
                      </TableCell>
                      <TableCell className={classes.tableHeader} align="center">
                        {i18n.t("utm.dashboard.table.contacts")}
                      </TableCell>
                      <TableCell className={classes.tableHeader} align="center">
                        {i18n.t("utm.dashboard.table.tickets")}
                      </TableCell>
                      <TableCell className={classes.tableHeader} align="center">
                        {i18n.t("utm.dashboard.table.open")}
                      </TableCell>
                      <TableCell className={classes.tableHeader} align="center">
                        {i18n.t("utm.dashboard.table.closed")}
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        {i18n.t("utm.dashboard.table.firstContact")}
                      </TableCell>
                      <TableCell className={classes.tableHeader}>
                        {i18n.t("utm.dashboard.table.lastContact")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.map((metric, index) => (
                      <TableRow key={index}>
                        <TableCell>{metric.utm_source || "-"}</TableCell>
                        <TableCell>{metric.utm_campaign || "-"}</TableCell>
                        <TableCell align="center">{metric.contacts || 0}</TableCell>
                        <TableCell align="center">{metric.tickets || 0}</TableCell>
                        <TableCell align="center">{metric.openTickets || 0}</TableCell>
                        <TableCell align="center">{metric.closedTickets || 0}</TableCell>
                        <TableCell>
                          {metric.firstContact
                            ? format(new Date(metric.firstContact), "dd/MM/yyyy HH:mm")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {metric.lastContact
                            ? format(new Date(metric.lastContact), "dd/MM/yyyy HH:mm")
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default UTMDashboard;
